//
// ============================================================================
// IMPORTS
// ============================================================================
//

import { unwrapPaged } from "~/services/api/domains/_shared/pagination";
import type { Paged } from "~/services/api/domains/_shared/pagination.types";

//
// ============================================================================
// TYPES
// ============================================================================
//

/**
 * Fetches a single page of a paged endpoint.
 *
 * @param page - 1-based page number to request.
 * @param pageSize - Page size to request (≤ backend max of 100).
 * @param signal - Abort signal threaded through to the HTTP layer.
 */
export type FetchPageFn<T> = (page: number, pageSize: number, signal?: AbortSignal | null) => Promise<Paged<T>>;

export interface CollectAllPagesOptions {
  /** Abort signal propagated to every page request. */
  signal?: AbortSignal | null;
  /** Page size per request. Defaults to the backend maximum (100) for the fewest round-trips. */
  pageSize?: number;
  /** Maximum number of concurrent requests for pages 2..N. @default 4 */
  concurrency?: number;
  /** Defensive cap on total pages, in case the backend misreports `totalPages`. @default 1000 */
  maxPages?: number;
}

//
// ============================================================================
// HELPERS
// ============================================================================
//

/** Inclusive integer range `[start, end]`. Returns `[]` when `end < start`. */
function range(start: number, end: number): number[] {
  const out: number[] = [];
  for (let n = start; n <= end; n += 1) out.push(n);
  return out;
}

/**
 * Maps `items` through `fn` with a bounded number of in-flight promises.
 * Results preserve input order. Rejects on the first error (the caller's
 * abort signal cancels the rest).
 */
async function mapWithConcurrency<TIn, TOut>(
  items: TIn[],
  concurrency: number,
  fn: (item: TIn) => Promise<TOut>,
): Promise<TOut[]> {
  const results: TOut[] = new Array(items.length);
  let cursor = 0;

  async function worker(): Promise<void> {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      results[index] = await fn(items[index]!);
    }
  }

  const workers = Array.from({ length: Math.min(Math.max(concurrency, 1), items.length) }, () => worker());
  await Promise.all(workers);
  return results;
}

//
// ============================================================================
// COLLECTOR
// ============================================================================
//

/**
 * Fetches **every** page of a paged endpoint and returns the flattened items.
 *
 * Reads page 1 to learn `totalPages`, then loads pages 2..N with bounded
 * concurrency. Terminates precisely from `totalPages` (no wasted "empty page"
 * request and no off-by-one on exactly-full last pages). The abort signal is
 * propagated to every request, and `maxPages` guards against a misreporting
 * backend.
 *
 * Use this for grouped / aggregated views (Channels, Instruments) and for
 * lookup datasets that must hold the complete set. User-paged tables should use
 * the server-pagination engine instead.
 *
 * @param fetchPage - Loads one page; receives `(page, pageSize, signal)`.
 * @param options - Page size, concurrency, abort signal, and the safety cap.
 * @returns All items across every page, in page order.
 */
export async function collectAllPages<T>(
  fetchPage: FetchPageFn<T>,
  options: CollectAllPagesOptions = {},
): Promise<T[]> {
  const pageSize = options.pageSize ?? 100;
  const maxPages = options.maxPages ?? 1000;
  const concurrency = options.concurrency ?? 4;

  // `unwrapPaged` is the defensive boundary: it throws on a non-envelope and
  // guarantees `totalPages` is a finite integer >= 1, so the `|| 1` hack and the
  // `totalPages === 0`-is-falsy ambiguity are gone.
  const first = unwrapPaged<T>(await fetchPage(1, pageSize, options.signal));
  const totalPages = Math.min(first.page.totalPages, maxPages);
  if (totalPages <= 1) return first.items;

  const rest = await mapWithConcurrency(range(2, totalPages), concurrency, (page) =>
    fetchPage(page, pageSize, options.signal).then((response) => unwrapPaged<T>(response).items),
  );

  return [first.items, ...rest].flat();
}
